import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { SerializedMediaWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../../../common/types';
import { SMLFormContainer } from '../../../../SMLFormContainer/SMLFormContainer';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { PromoteMediaDraft } from '../../../../../music24/promote/draftStore';
import { WidgetEditionProps } from '../../props';
import { mediaBlockMessages } from '../mediaBlockMessages';
import { MediaBlockPick, pickOfUpload } from './mediaBlockPick';
import { MediaBlockProblemBox } from './MediaBlockProblemBox';
import { MediaLookOptions } from './MediaLookOptions';
import { MediaPlaybackOptions } from './MediaPlaybackOptions';
import { MediaSourceSection } from './MediaSourceSection';
import { useMediaBlockDraft } from './useMediaBlockDraft';
import { useMediaBlockPicker } from './useMediaBlockPicker';
import { useMediaBlockSong } from './useMediaBlockSong';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media block editor; each part uses its own slice of it */
import styles from './MediaWidgetEditionView.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Media block in the rail (design a2–a6, d1, d3): the file (a Studio file of the song, or an
 * upload), the Playback options of a video, and Look and text. A Studio file is stored as its asset
 * type and variant (or reel length), so the page always shows the newest ready revision.
 */
const MediaWidgetEditionViewComponent: React.FC<WidgetEditionProps<SerializedMediaWidget>> = ({
  widget,
  accountId,
  smartLinkId,
  saveWidget,
  deleteWidget,
  isLoading,
  error,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const draft = useMediaBlockDraft(widget);
  const { choose } = draft;
  const [isChoosing, setChoosing] = useState(draft.pick === null);
  const [isInvalid, setInvalid] = useState(false);
  const song = useMediaBlockSong({ accountId, smartLinkId, source: widget?.data.source ?? null });

  const pick = useCallback(
    (next: MediaBlockPick) => {
      choose(next);
      setChoosing(false);
      setInvalid(false);
    },
    [choose]
  );
  const onUploaded = useCallback((media: PromoteMediaDraft) => pick(pickOfUpload(intl, media)), [intl, pick]);
  const picker = useMediaBlockPicker({
    accountId,
    song: song.song,
    items: song.items,
    isSongLoading: song.isLoading,
    onUploaded,
  });

  const openChooser = useCallback(() => setChoosing(true), []);
  const openStudio = useCallback(
    () => router.push(SMLRoutes.promoteStudio, song.song === undefined ? undefined : { catalogItemUuid: song.song }),
    [router, song.song]
  );
  const deleteBlock = useCallback(() => deleteWidget(), [deleteWidget]);

  const submit = useCallback(() => {
    if (draft.data === null) {
      setInvalid(true);
      return;
    }
    saveWidget(draft.data, SmartLinkWidgetType.media).catch(console.error);
  }, [draft.data, saveWidget]);

  const backRouteParams = useMemo(() => ({ accountId, smartLinkId }), [accountId, smartLinkId]);
  // The warning is about the saved file. A new pick replaces it.
  const showsSavedProblem = widget !== undefined && draft.source === widget.data.source;

  return (
    <SMLFormContainer
      backRoute={SMLRoutes.details}
      backRouteParams={backRouteParams}
      title={intl.formatMessage(mediaBlockMessages.title)}
      onSubmit={submit}
      onDelete={widget ? deleteWidget : undefined}
      submitDisabled={draft.source === null}
      isLoading={isLoading}
      error={error}
    >
      <div className={styles.rail} data-testid="media-widget-form">
        {widget !== undefined && showsSavedProblem && (
          <MediaBlockProblemBox data={widget.data} onPickAnother={openChooser} onDelete={deleteBlock} />
        )}
        <MediaSourceSection
          items={song.items}
          song={song.song}
          onSong={song.changeSong}
          picker={picker}
          pick={draft.pick}
          source={draft.source}
          isChoosing={isChoosing}
          onChoose={openChooser}
          onPick={pick}
          onOpenStudio={openStudio}
        />
        {draft.kind === 'video' && <MediaPlaybackOptions options={draft.options} setOption={draft.setOption} />}
        <MediaLookOptions kind={draft.kind} options={draft.options} setOption={draft.setOption} />
        {isInvalid && (
          <p className={styles.invalidLine} role="alert">
            {intl.formatMessage(draft.source === null ? mediaBlockMessages.noFile : mediaBlockMessages.invalid)}
          </p>
        )}
      </div>
    </SMLFormContainer>
  );
};

export const MediaWidgetEditionView = TypedMemo(MediaWidgetEditionViewComponent);
